import {useCallback, useEffect, useState} from 'react';

import miaFlowAuth from '../auth/MiaFlowAuth';
import {useSelectedOrganization} from '../context/OrganizationContext';

/**
 * Derives MainApp's "tenant" (the project-ownership scope) directly from
 * the Authorization organization selector (OrganizationSelector.jsx) —
 * per explicit instruction, projects are scoped by USER + the selected
 * ORGANIZATION, nothing else. There is exactly one selector in the
 * sidebar now.
 *
 * History (why this took two tries): an earlier version of this hook did
 * the same thing and broke project creation with a live-confirmed 403
 * "RBAC policy evaluation failed" on GET .../project-blueprint, followed
 * by a 400 "Missing required parameters" on POST .../projects/create — the
 * Authorization organization id and the Console "company" id are genuinely
 * different id spaces on this platform, and the BFF's create route
 * required a real `templateId` (which only the blueprint call could
 * supply) even for vfs-tier projects that never actually use it.
 * Root-caused and fixed at the source instead of reintroducing a second
 * tenant concept here: mia-flow-bff's POST /api/mia-platform/projects/create
 * no longer requires templateId (see that route's own comment) — vfs
 * creation only ever stored it as an inert label. The blueprint fetch this
 * comment used to describe as "still attempted best-effort" has since been
 * removed entirely from createQuickVfsProject — it always 403'd in
 * practice and its result was never used for anything real, just a
 * pointless request on every project creation.
 *
 * Return shape matches the old fetch-based hook on purpose, so every
 * existing consumer (MainApp, NavigationSidebar, handleStartChatFromHome's
 * fallbacks) keeps working without changes of their own.
 */
export function useTenantSelection() {
    const {selectedOrganization} = useSelectedOrganization();
    const [isTenantSwitching, setIsTenantSwitching] = useState(false);
    const switchingToTenantName = '';

    const selectedTenant = selectedOrganization?.tenant
        ? {
            tenantId: selectedOrganization.tenant,
            id: selectedOrganization.tenant,
            name: selectedOrganization.tenantName || selectedOrganization.tenant,
        }
        : null;
    const tenants = selectedTenant ? [selectedTenant] : [];

    // Keep the legacy miaFlowAuth singleton in sync — MainChat (a sibling
    // route component with no prop access to this hook) still reads
    // `miaFlowAuth.getSelectedTenant()` directly in a few places.
    useEffect(() => {
        if (selectedTenant) {
            miaFlowAuth.setSelectedTenant(selectedTenant);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTenant?.tenantId]);

    // No-ops: there is no separate tenant left to silently or explicitly
    // switch between (the organization selector owns that now).
    const selectTenantSilently = useCallback(() => {}, []);
    const handleTenantChange = useCallback(() => {}, []);

    return {
        tenants,
        selectedTenant,
        isLoadingTenants: false,
        isTenantSwitching,
        setIsTenantSwitching,
        switchingToTenantName,
        selectTenantSilently,
        handleTenantChange,
    };
}
